import React, { useState, useContext, useEffect } from 'react'
import InputBase from '@mui/material/InputBase';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import { DropzoneArea } from 'react-mui-dropzone'

import { HospitalContext } from '../../contexts/HospitalContext';
import ManageHospitalUsersForm from './ManageHospitalUsersForm'

import { uploadImages } from '../../helpers/uploadImages'
import { geocodeAddress, locationPayload, GEOCODE_FAILED_NOTICE } from '../../helpers/geocode'
import Loader from '../Loader'
import './CMSForm.css'
window.Buffer = window.Buffer || require("buffer").Buffer;

const stateNames = [
  'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut',
  'Delaware', 'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa', 'Kansas',
  'Kentucky', 'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan', 'Minnesota',
  'Mississippi', 'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire', 'New Jersey',
  'New Mexico', 'New York', 'North Carolina', 'North Dakota', 'Ohio', 'Oklahoma', 'Oregon',
  'Pennsylvania', 'Rhode Island', 'South Carolina', 'South Dakota', 'Tennessee', 'Texas', 'Utah',
  'Vermont', 'Virginia', 'Washington', 'West Virginia', 'Wisconsin', 'Wyoming'
];

const defaultData = {
  name: '',
  address: '',
  city: '',
  state: '',
  zip: '',
  logo: '',
  hospital_color_primary: '',
  hospital_color_secondary: ''
}


const ManageHospitalForm = ({ closeForm, initialData }) => {
  const { createHospital, editHospital } = useContext(HospitalContext)
  const [inputData, setInputData] = useState(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState('')
  // Set after a successful save that could not be geocoded; holds the form open.
  const [notice, setNotice] = useState('')

  useEffect(() => {
    if (initialData && Object.keys(initialData).length > 0) {
      let color = null
      try {
        color = initialData.color && JSON.parse(initialData.color)
      } catch {
        color = null
      }
      setInputData({
        ...initialData,
        hospital_color_primary: color && color.primary ? color.primary : '',
        hospital_color_secondary: color && color.secondary ? color.secondary : '',
      })
    }
    else {
      setInputData(defaultData)
    }
  }, [initialData])


  function handleChange(e) {
    const { name, value } = e.target;

    setInputData({
      ...inputData,
      [name]: value,
    });
  }

  function handleCloseForm(e) {
    // mousedown (not click): a failed save unmounts the spinner under the cursor,
    // and the leftover click would otherwise hit this overlay and dismiss the form.
    if (isSubmitting) return
    if (e.target === e.currentTarget) closeForm()
  }

  function isValidated() {
    if (inputData.name === '' || inputData.address === '' || inputData.city === '' || inputData.state === '' || inputData.zip === '' || inputData.hospital_color_primary === '' || inputData.hospital_color_secondary === '') {
      return false
    }
    return true
  }

  async function handleClick() {
    if (!isValidated()) {
      return
    }

    setIsSubmitting(true)
    setError('')
    try {
      // null when the address can't be geocoded — never blocks the save.
      let locationData = await geocodeAddress(inputData)
      let url = inputData?.logo?.file && await uploadImages([inputData.logo])

      let data = {
        ...inputData
        // location/cords/formatted_address are omitted when geocoding failed.
        // 'long-lat' is correct GeoJSON: organizations have no legacy location data
        // and nothing queries it geospatially, so no migration is involved.
        ,...locationPayload(locationData, { includeCordsString: true, coordinateOrder: 'long-lat' })
        ,logo: inputData.logo?.file ? url[0]?.url : inputData.logo ? inputData.logo : null
        ,color: JSON.stringify({ primary: inputData.hospital_color_primary, secondary: inputData.hospital_color_secondary })
      }

      if (initialData && Object.keys(initialData).length > 0) {
        await editHospital(initialData._id, data)
      }
      else {
        await createHospital(data)
      }

      // Saved either way. Hold the form open only to report the missing coordinates.
      if (!locationData) {
        setNotice(GEOCODE_FAILED_NOTICE)
        return
      }
      closeForm()
    } catch (err) {
      const errorData = err.response?.data
      setError(
        errorData?.errors?.length
          ? errorData.errors.join(' ')
          : errorData?.msg || errorData?.message || errorData?.error || (typeof errorData === 'string' ? errorData : 'Failed to save hospital.')
      )
    } finally {
      setIsSubmitting(false)
    }
  }

  function handleImgChange(e, index) {
    if (e[0]) {
      setInputData({
        ...inputData,
        logo: {
          name: e[0].name, url: null, file: e[0]
        }
      });
    }
  }

  return inputData && Object.keys(inputData).length > 0 && (
    <div onMouseDown={handleCloseForm} className="modal-overlay">
      <div className="manage-facility-form">

        <h3 className="manage-facility-form-title">Create/Edit Hospital</h3>
        <label htmlFor="name">Name</label>
        <InputBase className='manger-facility-input' value={inputData.name} onChange={(e) => handleChange(e)} type="text" name="name" id="name" placeholder='Name' />

        <label htmlFor="address">Address</label>
        <InputBase className='manger-facility-input' value={inputData.address} onChange={(e) => handleChange(e)} type="text" name="address" id="address" placeholder='Address' />

        <label htmlFor="city">City</label>
        <InputBase className='manger-facility-input' value={inputData.city} onChange={(e) => handleChange(e)} type="text" name="city" id="city" placeholder='City' />

        <label htmlFor="state">State</label>
        <FormControl fullWidth>
          <Select
            className="manger-facility-input"
            value={inputData.state}
            name="state"
            id="state"
            onChange={(e) => handleChange(e)}
          >
            {stateNames.map(e => <MenuItem key={e} value={e}>{e}</MenuItem>)}
          </Select>
        </FormControl>

        <label htmlFor="zip">Zip</label>
        <InputBase className='manger-facility-input' value={inputData.zip} onChange={(e) => handleChange(e)} type="number" name="zip" id="zip" placeholder='Zip' />

        <label htmlFor="logo">Logo</label>
        <div style={{ marginBottom: 20 }}>
          <DropzoneArea
            acceptedFiles={['image/jpeg', 'image/png']}
            showPreviews={false}
            showPreviewsInDropzone={false}
            maxFileSize={5000000}
            filesLimit={1}
            // multiple={false}
            onChange={(e) => handleImgChange(e)}
          />
          <p>{inputData?.logo?.name ? inputData?.logo?.name : inputData?.logo ? inputData.logo : ''}</p>
        </div>

        <label>Color Pallet</label>
        <InputBase className='manger-facility-input' value={inputData.hospital_color_primary} onChange={(e) => handleChange(e)} type="text" name="hospital_color_primary" id="hospital_color_primary" placeholder='Primary Color' />
        <InputBase className='manger-hospital-input' value={inputData.hospital_color_secondary} onChange={(e) => handleChange(e)} type="text" name="hospital_color_secondary" id="facility_color_secondary" placeholder='Secondary Color' />


        {initialData?._id && (
          <div className="org-section">
            <label className="org-section-label">Users in this organization</label>
            <ManageHospitalUsersForm hospital_id={initialData._id} closeForm={closeForm} />
          </div>
        )}

        {error && <p style={{ textAlign: 'center', margin: '5px 0px', color: '#E23844' }}>{error}</p>}
        {notice && <p style={{ textAlign: 'center', margin: '5px 0px', color: '#B26A00' }}>{notice}</p>}
        {notice ?
          <Button style={{ marginTop: 10 }} className='manger-facility-btn' onClick={() => closeForm()}>Close</Button>
          :
          <Button style={{ marginTop: 10 }} className='manger-facility-btn' disabled={isSubmitting} onClick={() => handleClick()}>{isSubmitting ? 'Submitting...' : 'Submit'}</Button>
        }
      </div>
      {isSubmitting && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 9999,
          flexDirection: 'column',
          gap: '20px'
        }}>
          <Loader loading={true} height={60} />
          <div style={{ color: 'white', fontSize: '18px', fontWeight: 'bold' }}>
            Submitting form...
          </div>
        </div>
      )}
    </div>
  )
}

export default ManageHospitalForm

